import React, { useContext, useMemo, useCallback, useEffect } from 'react';

import useLocalStorage from 'hooks/useLocalStorage';
import FeatureTogglesService from 'services/featureToggles';

import { featureToggleType } from 'config/featureToggles';

/**
 * @types
 * 
 * type FeatureToggle {
 *  id: number;
 *  location_id: number;
 *  config_name: string;
 *  attribute: string;
 *  val: string | boolean;
 * }
 */

const parseAttributeValue = (attribute, value, item) => {
  switch (attribute) {
    case featureToggleType.VISIBLE:
      return value === 'true';

    case featureToggleType.DISABLED:
      return value === 'true';

    case featureToggleType.VARIANT:
      return value;

    default:
      console.error(`Cannot parse value for feature toggle attribute ${attribute}`);
  }
}

const FeatureToggleContext = React.createContext(null);

export const useFeatureToggles = () => {
  return useContext(FeatureToggleContext);
}

export const useFeatureToggleItems = (prefix = '') => {
  const { featureToggles } = useFeatureToggles();

  return useMemo(() => {
    const itemsMap = (featureToggles || [])
      .filter(featureToggle => featureToggle.config_name.startsWith(prefix))
      // group attributes to single feature toggle item with the same name
      .reduce((itemsMap, featureToggle) => {
        const itemName = featureToggle.config_name.substring(
          featureToggle.config_name === prefix
            ? featureToggle.config_name.lastIndexOf('.') + 1
            : prefix.length + 1
        );

        const item = itemsMap[itemName] || { itemName, item: featureToggle.config_name };
        const { attribute, val } = featureToggle;
        const parsedValue = parseAttributeValue(attribute, val, item);

        if (parsedValue !== undefined) {
          item[attribute] = parsedValue;
          itemsMap[itemName] = item;
        }

        return itemsMap;
      }, {});

    const items = Object.values(itemsMap);
    console.debug(`Feature toggle items for prefix ${prefix}: `, items);
    return items;
  }, [featureToggles, prefix]);
};

export const useFeatureToggleItem = (featureToggleName) => {
  const items = useFeatureToggleItems(featureToggleName);
  return items[0];
};

export const FeatureToggleProvider = ({ children }) => {
  const hardwareId = useMemo(() => {
    if (window.location.search) {
      const params = new URLSearchParams(window.location.search.substring(1));
      return params.get('hw_id');
    }
  }, []);

  const featureTogglesService = useMemo(() => new FeatureTogglesService(), []);
  const [featureToggles, setFeatureToggles] = useLocalStorage('featureToggles', []);

  const fetchFeatureToggles = useCallback(() => {
    featureTogglesService.getFeatureFlags(hardwareId)
      .then(featureToggles => {
        setFeatureToggles([
          ...featureToggles,
          // NOTE: Left as an example
          // {
          //   "config_name": "ui.notifications.missing_tags",
          //   "attribute": "visible",
          //   "val": "false"
          // }
          // {
          //   config_name: 'ui.monitor.bigScreen.doors',
          //   attribute: 'variant',
          //   val: 'v2',
          // },
          // {
          //   config_name: 'ui.monitor.bigScreen.employees',
          //   attribute: 'visible',
          //   val: 'false',
          // }
          // {
          //   config_name: 'ui.monitor.btc',
          //   attribute: 'variant',
          //   val: 'v2',
          // },
          // {
          //   "config_name": "ui.feature.individuals",
          //   "attribute": "visible",
          //   "val": "false"
          // }
        ]);
      })
      .catch(error => {
        console.log('Failed to fetch feature toggles: ', error);
      });
  }, [featureTogglesService, setFeatureToggles, hardwareId]);

  const clearFeatureToggles = useCallback(() => {
    setFeatureToggles([]);
  }, [setFeatureToggles]);

  useEffect(() => {
    fetchFeatureToggles();
  }, [fetchFeatureToggles]);

  return (
    <FeatureToggleContext.Provider value={{ featureToggles, fetchFeatureToggles, clearFeatureToggles }}>
      {children}
    </FeatureToggleContext.Provider>
  )
};